/**
 * 69.23 (DECISIONS 241, the owner: "it would force bot behavior to be somewhat specialized in the mobile indirect fire
 * support role"): a bot squad leader calls its side's mortar — the crew never fires on its own (core/mortar.ts). On its look,
 * with a mortar of its side set, a leader on foot calls on the enemy it has seen with the most others it saw round it
 * (MORTAR_CALL.clusterPeople; the nearer on a tie), at least MORTAR_CALL.minPeople from itself and with none of its side
 * within the strike's reach of the point (the bots never call near their own, as with the airstrike). The Match answers the
 * call (its intent's `call`) or refuses it quietly — out of reach, or the mortar already firing a mission.
 * 69.42 (d) (DECISIONS 253 (2), with GameConfig.munitionsSupply): the calls paced to the bombs — the fewer the side's best
 * stocked mortar holds, the more enemies the call wants together (MORTAR_CALL.pace: its share of the store → the cluster), and
 * none with the bombs out; the last missions kept for a group, not spent on one rifleman.
 * 69.42 (f): only on enemies holding still — each seen at this look and the one before, moved under MORTAR_CALL.stillPeople a
 * second between them (a crawl is faster): the bombs land seconds after the call (the lay, the stagger, the flight), and
 * on Foothold four in five landed where one enemy or none still stood, the group called on gone. A group in place — at a
 * point, in cover, firing — is still there.
 */
import type { GameState, PlayerState, Vec3 } from '../types';
import { brainFace } from '../core/brainFace';
import { arcDistance } from '../core/gameState';
import { personSetOf, PERSON_BODY_HEIGHT } from '../core/scaleProfile';
import { friendliesInStrikeReach } from '../core/airstrike';
import { mortarsOf } from '../core/mortar';
import { MORTAR, PHYS_DT } from '../constants';

/** A leader's call: not on an enemy nearer itself than `minPeople` (danger close), the cluster counted within `clusterPeople` of it. */
export const MORTAR_CALL = { minPeople: 30, clusterPeople: 10, stillPeople: 0.8, pace: [{ share: 2 / 3, cluster: 1 }, { share: 1 / 3, cluster: 2 }, { share: 0, cluster: 3 }] as const };

/**
 * 69.42 (d): the enemies the call wants together on `planetIndex` — 1 without the munitions switch, else by the bombs of the
 * side's best stocked mortar there (MORTAR_CALL.pace); null with none left (or no mortar).
 */
export function mortarCluster(state: GameState, team: number, planetIndex: number): number | null {
  if (!state.config.munitionsSupply) return 1;
  const cap = state.config.mortarStore ?? MORTAR.store;
  const most = Math.max(0, ...mortarsOf(state, team).filter(s => s.planetIndex === planetIndex).map(s => s.mortar?.rounds ?? 0));
  if (most <= 0 || cap <= 0) return null;
  return MORTAR_CALL.pace.find(p => most >= p.share * cap)!.cluster;
}

/** Whether `unit` leads its squad (its first living member). */
function leads(state: GameState, unit: PlayerState): boolean {
  const sq = (state.squads ?? []).find(q => q.unitIds.includes(unit.id));
  return !!sq && sq.unitIds.find(id => state.players.some(p => p.id === id && p.alive)) === unit.id;
}

/** The point `unit` (a bot squad leader on foot, on its look, with a mortar of its side set) calls the mortar on, or null. */
export function mortarCallFor(state: GameState, unit: PlayerState): { planetIndex: number; surfaceDir: Vec3 } | null {
  if (!unit.alive || unit.mount || !leads(state, unit)) return null;
  const face = brainFace(state, unit.id);
  const person0 = PERSON_BODY_HEIGHT * personSetOf(state).k, R0 = state.planets[unit.planetIndex]?.radius ?? 1;
  // 69.42 (f): the ones holding still since the look before (none before: none to call on)
  const still = (x: { id: number; surfaceDir: Vec3 }) => {
    const was = face?.seenBefore?.find(y => y.id === x.id), dt = was && face?.seenTick !== undefined ? (face.seenTick - was.tick) * PHYS_DT : 0;
    return !!was && dt > 0 && arcDistance(was.surfaceDir, x.surfaceDir, R0) <= MORTAR_CALL.stillPeople * person0 * dt;
  };
  const seen = (face?.seenAt ?? []).filter(x => x.planetIndex === unit.planetIndex && still(x));
  if (!seen.length) return null;
  const want = mortarCluster(state, unit.teamIndex, unit.planetIndex);   // (69.42 (d): paced to the bombs)
  if (want === null || seen.length < want) return null;
  const planet = state.planets[unit.planetIndex];
  if (!planet) return null;
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k, R = planet.radius;
  let best: { at: Vec3; n: number; d: number } | null = null;
  for (const e of seen) {
    const d = arcDistance(unit.surfaceDir, e.surfaceDir, R);
    if (d < MORTAR_CALL.minPeople * person) continue;
    const n = seen.filter(o => arcDistance(o.surfaceDir, e.surfaceDir, R) <= MORTAR_CALL.clusterPeople * person).length;
    if (!best || n > best.n || (n === best.n && d < best.d)) best = { at: e.surfaceDir, n, d };
  }
  if (!best || best.n < want || friendliesInStrikeReach(state, unit.teamIndex, unit.planetIndex, best.at) > 0) return null;
  return { planetIndex: unit.planetIndex, surfaceDir: { ...best.at } };
}
